import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import React from 'react';
import PageTransition from '../components/common/PageTransition';
import Carousel from '../components/gallery/Carousel';
import ImageCaption from '../components/gallery/ImageCaption';
import SEO from '../components/common/SEO';
import { staggerContainer, staggerItem } from '../utils/animations';
import { galleryImages } from '../data/galleryImages';
import { FaWhatsapp } from 'react-icons/fa';

const galleryStats = [
  { icon: '📸', value: '500+', label: 'Photos', color: '#D32F2F' },
  { icon: '👧', value: '30K+', label: 'Happy Readers', color: '#3CB044' },
  { icon: '🎉', value: '50+', label: 'Events', color: '#F57C00' },
  { icon: '🎨', value: '200+', label: 'Artworks', color: '#7B2FBE' },
];

const categoryInfo = {
  all: { icon: '📚', label: 'All Photos' },
  readers: { icon: '👧', label: 'Happy Readers' },
  events: { icon: '🎉', label: 'Events' },
  artwork: { icon: '🎨', label: 'Artwork' },
  activities: { icon: '✂️', label: 'Activities' },
  team: { icon: '👥', label: 'Our Team' },
};

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredImages = activeCategory === 'all'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeCategory);

  const categories = ['all', ...new Set(galleryImages.map(img => img.category))];

  return (
    <PageTransition>
      <SEO
        title="Photo Gallery - Happy Readers & Activities"
        description="Browse photos of children participating in events, reading their issues, and showing off their creative activities with The Tiny Times."
        keywords="kids activities gallery, children reading photos, tiny times events, creative corner showcase"
      />
      <div className="min-h-screen">

        {/* Hero */}
        <section className="relative py-24 md:py-36 overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #FFF9E6 0%, #ECFDF0 40%, #EFF6FF 100%)' }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle, rgba(245,124,0,0.12) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
          <div className="absolute top-0 right-0 w-96 h-96 opacity-20 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(245,124,0,0.3), transparent 70%)', filter: 'blur(60px)', transform: 'translate(25%,-25%)' }} />

          {[{ e: '🖼️', x: '7%', y: '22%' }, { e: '📸', x: '87%', y: '18%' }, { e: '🌟', x: '80%', y: '72%' }, { e: '✨', x: '10%', y: '70%' }].map((item, i) => (
            <motion.div key={i} className="absolute text-5xl opacity-20 pointer-events-none select-none"
              style={{ left: item.x, top: item.y }}
              animate={{ y: [0, -20, 0], rotate: [0, 12, -12, 0] }}
              transition={{ duration: 4 + i * 0.4, repeat: Infinity, delay: i * 0.3 }}>
              {item.e}
            </motion.div>
          ))}

          <div className="container mx-auto px-4 relative z-10">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center">
              <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.3, type: 'spring', stiffness: 200 }} className="text-8xl mb-5">🖼️</motion.div>
              <h1 className="text-5xl md:text-7xl font-display font-bold mb-5 leading-tight" style={{ color: '#1A1F2E' }}>
                Our{' '}
                <span style={{ background: 'linear-gradient(135deg, #F57C00, #D32F2F)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Gallery
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-gray-dark leading-relaxed max-w-xl mx-auto">
                Explore magical moments from The Tiny Times! See our readers, events, and creative adventures 📸
              </p>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
            <svg viewBox="0 0 1440 80" className="w-full" preserveAspectRatio="none" style={{ display: 'block' }}>
              <path fill="white" d="M0,40L60,45C120,50,240,60,360,55C480,50,600,30,720,28C840,25,960,35,1080,40C1200,45,1320,42,1380,40L1440,38L1440,80L0,80Z" />
            </svg>
          </div>
        </section>

        {/* Stats Row */}
        <section className="py-10 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 max-w-4xl mx-auto">
              {galleryStats.map((stat, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="text-center rounded-2xl p-5"
                  style={{ background: `${stat.color}08`, border: `1px solid ${stat.color}18` }}>
                  <div className="text-4xl mb-2">{stat.icon}</div>
                  <div className="text-3xl font-display font-bold mb-1" style={{ color: stat.color }}>{stat.value}</div>
                  <div className="text-xs text-neutral-gray-dark font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Carousel */}
        <section className="py-14 md:py-20" style={{ background: '#F5F7FA' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-3" style={{ color: '#1A1F2E' }}>Featured Moments ✨</h2>
              <p className="text-lg text-neutral-gray-dark max-w-xl mx-auto">Swipe through our favourite memories!</p>
            </motion.div>
            <div className="max-w-6xl mx-auto">
              <Carousel images={galleryImages.filter(img => img.featured)} autoPlay={true} interval={4000} />
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-6 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => {
                const info = categoryInfo[category] || { icon: '📷', label: category };
                const isActive = activeCategory === category;
                return (
                  <motion.button key={category} onClick={() => setActiveCategory(category)}
                    whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-button font-display font-semibold text-sm transition-all"
                    style={isActive
                      ? { background: '#3CB044', color: 'white', boxShadow: '0 4px 14px rgba(60,176,68,0.3)' }
                      : { background: 'white', color: '#3CB044', border: '1.5px solid rgba(60,176,68,0.3)' }
                    }>
                    <span className="text-lg">{info.icon}</span>
                    <span className="capitalize">{info.label}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="py-14 md:py-24" style={{ background: '#FAFAFA' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-3" style={{ color: '#1A1F2E' }}>All Photos 📷</h2>
              <p className="text-lg text-neutral-gray-dark">Click on any image to view it in full size</p>
            </motion.div>

            <motion.div variants={staggerContainer} initial="initial" whileInView="animate" viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
              {filteredImages.map((image) => (
                <motion.div key={image.id} variants={staggerItem} whileHover={{ scale: 1.03, zIndex: 10 }}
                  className="relative group cursor-pointer" onClick={() => setSelectedImage(image)}>
                  <div className="relative aspect-square rounded-xl overflow-hidden shadow-card">
                    <img src={image.src} alt={image.alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <h3 className="font-display font-bold text-base mb-0.5 line-clamp-1">{image.title}</h3>
                        <p className="text-xs text-white/80 line-clamp-2">{image.caption}</p>
                      </div>
                    </div>
                    {image.category && (
                      <div className="absolute top-3 left-3 bg-[#F57C00] text-white text-xs font-display font-bold px-2.5 py-1 rounded-full shadow capitalize">{image.category}</div>
                    )}
                    {image.featured && (
                      <div className="absolute top-3 right-3 bg-amber-400 text-amber-900 text-xs font-display font-bold px-2.5 py-1 rounded-full shadow">⭐ Featured</div>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {filteredImages.length === 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
                <div className="text-8xl mb-4">📭</div>
                <h3 className="text-2xl font-display font-bold mb-2" style={{ color: '#1A1F2E' }}>No images in this category</h3>
                <p className="text-neutral-gray-dark">Try selecting a different category.</p>
              </motion.div>
            )}
          </div>
        </section>

        {/* Share Your Moments */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center">
              <div className="text-7xl mb-5">📤</div>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Share Your Moments!</h2>
              <p className="text-xl text-neutral-gray-dark mb-8 max-w-2xl mx-auto">
                Are you a Tiny Times reader? Share your photos reading our magazine, doing activities, or showing off your artwork!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a href="https://wa.me/919618377750" target="_blank" rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl text-white"
                  style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.3)' }}>
                  <FaWhatsapp className="text-2xl" /> Send Photos 📸
                </motion.a>
                <motion.a href="/subscribe" whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl"
                  style={{ background: 'white', color: '#3CB044', border: '2px solid rgba(60,176,68,0.3)' }}>
                  Subscribe Now 🚀
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20" style={{ background: 'linear-gradient(135deg, #1E7C24, #145218)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center">
              <div className="text-7xl mb-5">🌟</div>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">Be Part of Our Story!</h2>
              <p className="text-xl text-white/85 mb-8">Join 30,000+ happy readers and create your own magical moments!</p>
              <motion.a href="https://wa.me/919618377750" target="_blank" rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-3 px-10 py-4 rounded-button font-display font-bold text-xl text-white"
                style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.4)' }}>
                <FaWhatsapp className="text-2xl" /> Subscribe Today 🎉
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* Lightbox */}
        <AnimatePresence>
          {selectedImage && (
            <Lightbox image={selectedImage} images={filteredImages}
              onClose={() => setSelectedImage(null)} onNavigate={setSelectedImage} />
          )}
        </AnimatePresence>
      </div>
    </PageTransition>
  );
};

const Lightbox = ({ image, images, onClose, onNavigate }) => {
  const currentIndex = images.findIndex(img => img.id === image.id);
  const goToPrevious = () => { const i = currentIndex > 0 ? currentIndex - 1 : images.length - 1; onNavigate(images[i]); };
  const goToNext = () => { const i = currentIndex < images.length - 1 ? currentIndex + 1 : 0; onNavigate(images[i]); };

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => { document.body.style.overflow = 'unset'; window.removeEventListener('keydown', handleKeyDown); };
  }, [currentIndex]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95" onClick={onClose}>
      <button onClick={onClose}
        className="absolute top-4 right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors">
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <button onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
        className="absolute left-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors">
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button onClick={(e) => { e.stopPropagation(); goToNext(); }}
        className="absolute right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors">
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()} className="relative max-w-5xl max-h-[85vh] mx-4">
        <img src={image.src} alt={image.alt} className="max-w-full max-h-[75vh] object-contain rounded-xl" />
        <ImageCaption title={image.title} caption={image.caption} />
      </motion.div>
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/10 text-white px-4 py-2 rounded-full font-display text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </motion.div>
  );
};

export default Gallery;